import React from 'react';
import { Search, Filter, X, Calendar, Tag, SlidersHorizontal, ArrowUp, ArrowDown, Users, Building, Building2, Landmark } from 'lucide-react';
import { Pessoa } from '../../types/pessoa';
import { Banco } from '../../types/financeiro';
import { EmpresaConfig } from '../../types/empresa';
import { pessoaBelongsToGroup, GROUPS_LIST, GroupFilterType } from '../../utils/pessoaGroupUtils';

export interface FilterSortState {
  searchTerm: string;
  statusFilter: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
  categoryFilter?: string;
  groupFilter?: string;
  bancoFilter?: string;
  cnpjFilter?: string;
}

interface FilterOption {
  value: string;
  label: string;
}

interface SortOption {
  value: string;
  label: string;
}

interface FiltroEClassificacaoBarProps {
  filters: FilterSortState;
  onChange: (newFilters: FilterSortState) => void;
  statusOptions?: FilterOption[];
  sortOptions?: SortOption[];
  categoryOptions?: FilterOption[];
  pessoas?: Pessoa[];
  bancos?: Banco[];
  empresas?: EmpresaConfig[];
  showBankAndCnpj?: boolean;
  placeholderSearch?: string;
  showDateRange?: boolean;
  showCategories?: boolean;
  totalResults?: number;
}

export function FiltroEClassificacaoBar({
  filters,
  onChange,
  statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'abertas', label: 'Em Aberto / Pendentes' },
    { value: 'quitadas', label: 'Quitadas / Pagas' },
    { value: 'atrasadas', label: 'Atrasadas' },
  ],
  sortOptions = [
    { value: 'id', label: 'Código (ID)' },
    { value: 'data', label: 'Data de Vencimento / Emissão' },
    { value: 'valor', label: 'Valor (R$)' },
    { value: 'descricao', label: 'Descrição' },
  ],
  categoryOptions,
  pessoas,
  bancos = [],
  empresas = [],
  showBankAndCnpj = true,
  placeholderSearch = 'Pesquisar por descrição, cliente, ID...',
  showDateRange = true,
  showCategories = false,
  totalResults,
}: FiltroEClassificacaoBarProps) {
  const currentGroup = filters.groupFilter || 'all';

  // Filter pessoas list according to selected group
  const groupFilteredPessoas = React.useMemo(() => {
    if (!pessoas) return [];
    if (currentGroup === 'all') return pessoas;
    return pessoas.filter((p) => pessoaBelongsToGroup(p, currentGroup));
  }, [pessoas, currentGroup]);

  const hasActiveFilters =
    filters.searchTerm !== '' ||
    filters.statusFilter !== 'all' ||
    (filters.startDate && filters.startDate !== '') ||
    (filters.endDate && filters.endDate !== '') ||
    (filters.groupFilter && filters.groupFilter !== 'all') ||
    (filters.categoryFilter && filters.categoryFilter !== 'all') ||
    (filters.bancoFilter && filters.bancoFilter !== 'all') ||
    (filters.cnpjFilter && filters.cnpjFilter !== 'all');

  const handleClear = () => {
    onChange({
      ...filters,
      searchTerm: '',
      statusFilter: 'all',
      startDate: '',
      endDate: '',
      groupFilter: 'all',
      categoryFilter: 'all',
      bancoFilter: 'all',
      cnpjFilter: 'all',
      sortBy: 'id',
      sortOrder: 'desc',
    });
  };

  const handleGroupChange = (newGroup: string) => {
    onChange({
      ...filters,
      groupFilter: newGroup,
      // reset specific person selection if it doesn't belong to new group
      categoryFilter: 'all',
    });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3 transition-all">
      {/* Top Controls Row */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[240px]">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-4 w-4 text-slate-400" />
          </div>
          <input
            type="text"
            className="block w-full rounded-lg border border-slate-300 pl-9 pr-8 py-2 text-xs sm:text-sm text-slate-800 focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-slate-50/50 hover:bg-white transition-colors"
            placeholder={placeholderSearch}
            value={filters.searchTerm}
            onChange={(e) => onChange({ ...filters, searchTerm: e.target.value })}
          />
          {filters.searchTerm && (
            <button
              onClick={() => onChange({ ...filters, searchTerm: '' })}
              className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 hover:text-slate-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter & Sort Controls Group */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter Dropdown */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors">
            <Filter className="h-3.5 w-3.5 text-slate-500" />
            <select
              className="bg-transparent border-none outline-none cursor-pointer pr-1 text-xs font-semibold text-slate-800"
              value={filters.statusFilter}
              onChange={(e) => onChange({ ...filters, statusFilter: e.target.value })}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Classification / Sort By Dropdown */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors">
            <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-slate-400 font-normal hidden sm:inline">Ordenar:</span>
            <select
              className="bg-transparent border-none outline-none cursor-pointer pr-1 text-xs font-semibold text-slate-800"
              value={filters.sortBy}
              onChange={(e) => onChange({ ...filters, sortBy: e.target.value })}
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Order Toggle (Asc/Desc) */}
          <button
            type="button"
            onClick={() =>
              onChange({
                ...filters,
                sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc',
              })
            }
            title={filters.sortOrder === 'asc' ? 'Ordem Crescente (A-Z / Menor)' : 'Ordem Decrescente (Z-A / Maior)'}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
          >
            {filters.sortOrder === 'asc' ? (
              <>
                <ArrowUp className="h-3.5 w-3.5 text-emerald-600" />
                <span>Crescente</span>
              </>
            ) : (
              <>
                <ArrowDown className="h-3.5 w-3.5 text-red-600" />
                <span>Decrescente</span>
              </>
            )}
          </button>

          {/* Group Filter Dropdown */}
          {showCategories && (
            <div className="flex items-center gap-1 bg-slate-50 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors">
              <Users className="h-3.5 w-3.5 text-red-600" />
              <select
                className="bg-transparent border-none outline-none cursor-pointer pr-1 text-xs font-semibold text-slate-800"
                value={filters.groupFilter || 'all'}
                onChange={(e) => handleGroupChange(e.target.value)}
              >
                {GROUPS_LIST.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Person / Category Filter Dropdown (Filtered by selected group) */}
          {showCategories && (
            <div className="flex items-center gap-1 bg-slate-50 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors max-w-[260px]">
              <Tag className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <select
                className="bg-transparent border-none outline-none cursor-pointer pr-1 text-xs font-semibold text-slate-800 truncate w-full"
                value={filters.categoryFilter || 'all'}
                onChange={(e) => onChange({ ...filters, categoryFilter: e.target.value })}
              >
                <option value="all">
                  {currentGroup !== 'all'
                    ? `Todos de ${GROUPS_LIST.find((g) => g.id === currentGroup)?.label.split('&')[0].trim()}`
                    : 'Todas Pessoas / Empresas'}
                </option>
                {pessoas && pessoas.length > 0
                  ? groupFilteredPessoas.map((p) => (
                      <option key={p.id} value={p.id.toString()}>
                        {p.nome} {p.documento ? `(${p.documento})` : ''}
                      </option>
                    ))
                  : categoryOptions?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
              </select>
            </div>
          )}

          {/* CNPJ Filter Dropdown */}
          {showBankAndCnpj && (
            <div className="flex items-center gap-1 bg-slate-50 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors">
              <Building2 className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
              <select
                className="bg-transparent border-none outline-none cursor-pointer pr-1 text-xs font-semibold text-slate-800"
                value={filters.cnpjFilter || 'all'}
                onChange={(e) => onChange({ ...filters, cnpjFilter: e.target.value })}
              >
                <option value="all">Todos os CNPJs</option>
                {empresas.map((emp, idx) => {
                  const label = emp.cnpj && emp.cnpj.length > 5 
                    ? `${emp.cnpj} - ${emp.nome_fantasia || emp.razao_social}` 
                    : emp.nome_fantasia || emp.razao_social || `Empresa ${idx + 1}`;
                  return (
                    <option key={emp.id} value={label}>{label}</option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Banco Filter Dropdown */}
          {showBankAndCnpj && (
            <div className="flex items-center gap-1 bg-slate-50 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors">
              <Landmark className="h-3.5 w-3.5 text-blue-600 shrink-0" />
              <select
                className="bg-transparent border-none outline-none cursor-pointer pr-1 text-xs font-semibold text-slate-800"
                value={filters.bancoFilter || 'all'}
                onChange={(e) => onChange({ ...filters, bancoFilter: e.target.value })}
              >
                <option value="all">Todos os Bancos</option>
                <option value="Banco do Brasil">Banco do Brasil</option>
                <option value="BTG">BTG Pactual</option>
                <option value="Bradesco">Bradesco</option>
                <option value="Santander">Santander</option>
                <option value="Itaú">Itaú</option>
                {bancos.map((b) => (
                  <option key={b.id} value={b.nome}>
                    {b.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={handleClear}
              type="button"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Date Range Row */}
      {showDateRange && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100 text-xs">
          <div className="flex flex-wrap items-center gap-2 text-slate-600">
            <span className="font-semibold text-slate-700 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-slate-400" />
              Período:
            </span>
            <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
              <span className="text-slate-400 text-[10px] uppercase font-bold">De</span>
              <input
                type="date"
                className="bg-transparent border-none outline-none text-xs text-slate-800 cursor-pointer"
                value={filters.startDate || ''}
                onChange={(e) => onChange({ ...filters, startDate: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
              <span className="text-slate-400 text-[10px] uppercase font-bold">Até</span>
              <input
                type="date"
                className="bg-transparent border-none outline-none text-xs text-slate-800 cursor-pointer"
                value={filters.endDate || ''}
                onChange={(e) => onChange({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>

          {totalResults !== undefined && (
            <div className="text-slate-500 font-medium flex items-center gap-2">
              {currentGroup !== 'all' && (
                <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-[11px] font-semibold border border-red-200">
                  Filtro: {GROUPS_LIST.find((g) => g.id === currentGroup)?.label}
                </span>
              )}
              <span>
                Exibindo <span className="font-bold text-slate-800">{totalResults}</span> registro(s)
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
